import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Paper,
    Typography,
    Tooltip,
    CircularProgress,
    } from "../../ui";
import { useDispatch,
    useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { snpAxios } from "../../global/api";
import {
  CART_INITIAL_STATE,
  SHOWUSERTABLE,
  SHOWALLBILLS,
  getPreUrlAccordingToCart,
  SUCCESS,
  InternalBillType,
  BillStatus,
  TRIP_RUNNING,
  isEmpty
} from "../../../constants/constant";
import DismissButton from "../../global/DismissButton";
import SnpButton from "../../global/SnpButton";
import { fetchBillDetails1, fetchBillItems, fetchBillPaymentDetails, fetchBillPartialPayDetails } from "../../../store/billDetailsSlice";
import KeyboardBackspaceIcon from "../../ui/icons/KeyboardBackspace";
import TableRestaurantIcon from "../../ui/icons/TableRestaurant";
import "../BillingModule.css";

const AllTableViewDialog = ({ showDialog, setShowDialog }) => {
  const [tableData, setTableData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [activeView, setActiveView] = useState(InternalBillType.BookTable);
  const [gridBlockLoader, setGridBlockLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const storeActiveShopHash = useSelector(
    (state) => state.setActiveShopHashReducer.activeShopHash
  );
  const storeApiUrl = useSelector(
    (state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL
  );

  const getTableData = useCallback(async () => {
    try {
      setGridBlockLoader(true);
      const custUrl = `${storeApiUrl.BACKEND_BASE_URL}${getPreUrlAccordingToCart(storeActiveShopHash.cartType)}${SHOWUSERTABLE}`;
      const data = {
        jcJson: {
          value: CART_INITIAL_STATE.apiToken,
          shopHash: storeActiveShopHash.id,
        },
      };
      const response = await snpAxios.post(custUrl, data);
      if (response.data.status === SUCCESS) {
        setTableData(response.data.jcJson.value_2);
      } else {
        setTableData([]);
      }
      setGridBlockLoader(false);
    } catch (e) {
      console.error("AllTableViewDialog getTableData error:", e);
      setGridBlockLoader(false);
      setTableData([]);
    }
  }, [storeApiUrl, storeActiveShopHash]);

  useEffect(() => {
    if (showDialog) {
      setActiveView(InternalBillType.BookTable);
      getTableData();
    }
  }, [showDialog, getTableData]);

  const getOrderData = async (billType) => {
    try {
      setGridBlockLoader(true);
      const response = await snpAxios.post(
        `${storeApiUrl.BACKEND_BASE_URL}${SHOWALLBILLS}`,
        {
          jcJson: {
            shopHash: storeActiveShopHash.id,
            value: CART_INITIAL_STATE.apiToken,
            settleBill: false,
            billType,
            myBillRequest: false,
          },
        },
      );
      setOrderData(response.data.status === SUCCESS ? response.data.jcJson.data || [] : []);
    } catch (e) {
      console.error("AllTableViewDialog getOrderData error:", e);
      setOrderData([]);
    } finally {
      setGridBlockLoader(false);
    }
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === InternalBillType.BookTable) {
      getTableData();
    } else {
      getOrderData(view);
    }
  };

  const handleTableSelect = (table) => {
    setShowDialog(false);
    console.log("Selected table from bill screen:", table);
    navigate("/dashboard/billentry", {
      state: {
        tableProps: table,
        sBillNo: table.billNo || "",
        billType: InternalBillType.BookTable,
      },
    });
  };

  const handleNewOrder = (billType) => {
    setShowDialog(false);
    navigate("/dashboard/billentry", {
      state: {
        tableProps: {},
        sBillNo: "",
        billType,
      },
    });
  };

  const handleOrderSelect = (order) => {
    setShowDialog(false);
    navigate("/dashboard/billentry", {
      state: {
        tableProps: {},
        sBillNo: order.billNo,
        billType: activeView,
      },
    });
  };

  const getBillDets = (level, data) => {
    if (level === 1) {
      let details = data.billNo;
      if (data.delvName) details += `(${data.delvName})`;
      return details;
    }
    if (level === 2) return `Order no.${data.token}(₹${data.finalPrice})`;
    if (level === 3 && data.lapsedTime) return data.lapsedTime;
    if (level === 4) {
      let details = "";
      if (data.billAddBy && data.billAddBy !== CART_INITIAL_STATE.userName) {
        details = `@ ${data.billAddBy}`;
      }
      if (data.custName) details += `${details ? "\n" : ""}Cust:${data.custName}`;
      if (data.tripDetails) details += `\nTrip Status: ${data.tripDetails}`;
      return details;
    }
    return "";
  };

  const isPaidOrder = (data) => (
    (data.billStatus === BillStatus.PAID && isEmpty(data.tripDetails))
    || (data.billStatus === BillStatus.HOMEDELIVERYPAY
      && (isEmpty(data.tripDetails) || data.tripDetails === TRIP_RUNNING))
  );

  const getTableDetsName = (level, data) => {
    if (level === 1) {
      let resp = "";
      if (!isEmpty(data.tableName))
        resp = data.tableName + "(" + data.tableNo + ")";
      else resp = "T no." + data.tableNo;
      if (!isEmpty(data.finalPrice)) resp = resp + "\n₹" + data.finalPrice;
      return resp;
    }
    if (level === 2 && data.lapsedTime) return data.lapsedTime;
    if (level === 3) {
      let resp = "";
      if (data.assignedName) resp = "@ " + data.assignedName;
      if (data.saleName) resp = resp + "\n# " + data.saleName;
      if (data.custName) resp = resp + "\nCust:" + data.custName;
      return resp;
    }
    return "";
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => setShowDialog(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontSize: "16px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <TableRestaurantIcon />
        All Tables View
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <SnpButton
              fullWidth
              variant="contained"
              mcolor="#17221d"
              mbgcolor="rgb(252, 221, 165)"
              onClick={() => handleViewChange(InternalBillType.BookTable)}
              sx={{ opacity: activeView === InternalBillType.BookTable ? 1 : 0.62 }}
            >
              Table
            </SnpButton>
          </Grid>
          <Grid item xs={12} sm={4}>
            <SnpButton
              fullWidth
              variant="contained"
              mcolor="#17221d"
              mbgcolor="#bfb2ce"
              onClick={() => handleViewChange(InternalBillType.TakeAway)}
              sx={{ opacity: activeView === InternalBillType.TakeAway ? 1 : 0.62 }}
            >
              Take Away
            </SnpButton>
          </Grid>
          <Grid item xs={12} sm={4}>
            <SnpButton
              fullWidth
              variant="contained"
              mcolor="#17221d"
              mbgcolor="#bdecf7"
              onClick={() => handleViewChange(InternalBillType.Home)}
              sx={{ opacity: activeView === InternalBillType.Home ? 1 : 0.62 }}
            >
              Home Delivery
            </SnpButton>
          </Grid>
        </Grid>
        {gridBlockLoader && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: 200 }}
          >
            <CircularProgress />
          </Grid>
        )}
        {!gridBlockLoader
          && activeView === InternalBillType.BookTable
          && tableData.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            No tables found
          </Typography>
        )}
        {!gridBlockLoader
          && activeView !== InternalBillType.BookTable
          && orderData.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            No active orders found
          </Typography>
        )}
        {!gridBlockLoader
          && activeView === InternalBillType.BookTable
          && tableData.length > 0 && (
          <Grid container spacing={1.5}>
            {tableData.map((data) => (
              <Grid
                item
                md={1.5}
                xs={6}
                sm={3}
                key={data.id}
                sx={{ padding: 0.5 }}
              >
                <Paper
                  elevation={3}
                  onClick={() => handleTableSelect(data)}
                  sx={{
                    borderRadius: 2,
                    p: 1,
                    cursor: "pointer",
                    backgroundColor: data.tableBooked ? "#ff9800" : "#e8f5e9",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: 6,
                    },
                    textAlign: "center",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    fontSize: "12px",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    fontSize="13px"
                  >
                    {getTableDetsName(1, data)}
                  </Typography>
                  <Typography variant="caption" display="block" fontSize="10px">
                    {getTableDetsName(2, data)}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    fontSize="10px"
                    sx={{ mt: 0.5 }}
                  >
                    {getTableDetsName(3, data)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
        {!gridBlockLoader
          && activeView !== InternalBillType.BookTable
          && orderData.length > 0 && (
          <Grid container spacing={1.5}>
            {orderData.map((data) => (
              <Grid item md={2} xs={6} sm={4} key={data.billNo}>
                <Paper
                  elevation={0}
                  onClick={() => handleOrderSelect(data)}
                  className="showbills-data-container"
                  sx={{
                    borderRadius: 4,
                    cursor: "pointer",
                    backgroundColor: isPaidOrder(data)
                      ? "#a1b487"
                      : data.billStatus === BillStatus.PARTIALLYPAID
                        ? "#f08888"
                        : "white",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "scale(1.03)", boxShadow: 6 },
                  }}
                >
                  <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word", textAlign: "center" }}>
                    <p style={{ fontSize: "10px" }}>{getBillDets(1, data)}</p>
                    <p style={{ fontSize: "12px" }}><b>{getBillDets(2, data)}</b></p>
                    <p style={{ fontSize: "10px" }}>{getBillDets(3, data)}</p>
                    <p style={{ fontSize: "10px" }}>{getBillDets(4, data)}</p>
                  </div>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
        <SnpButton
          variant="contained"
          mbgcolor="#543d4e"
          onClick={() => handleNewOrder(InternalBillType.TakeAway)}
        >
          New Take Away
        </SnpButton>
        <SnpButton
          variant="contained"
          mbgcolor="#167a96"
          onClick={() => handleNewOrder(InternalBillType.Home)}
        >
          New Home Delivery
        </SnpButton>
        <DismissButton
          variant="contained"
          onClick={() => setShowDialog(false)}
        >
          Close
        </DismissButton>
      </DialogActions>
    </Dialog>
  );
};

export default AllTableViewDialog;
